import React, { useState } from "react";
import { http } from "@/lib/api";
import { X, UploadSimple, FileXls, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ImportContactsModal({ onClose, onImported, services, users, currentUser, onServicesChanged }) {
  const [step, setStep] = useState(1); // 1: upload, 2: preview + services, 3: done
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [newSvc, setNewSvc] = useState("");
  const canAddService = currentUser?.role === "admin" || currentUser?.role === "manager";

  const parse = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await http.post("/contacts/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.count === 0) {
        toast.error("File kosong atau tidak ada baris valid (butuh kolom 'name')");
        setLoading(false);
        return;
      }
      setRows(data.preview);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memproses file");
    } finally {
      setLoading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    parse(f);
  };

  const toggleService = (name) => {
    setSelectedServices((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  };

  const addService = async () => {
    const name = newSvc.trim();
    if (!name) return;
    try {
      await http.post("/services", { name });
      toast.success(`"${name}" ditambahkan`);
      setNewSvc("");
      await onServicesChanged?.();
      setSelectedServices((s) => [...new Set([...s, name])]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/contacts/import/commit", {
        rows,
        services: selectedServices,
        owner_id: ownerId || null,
      });
      setImportedCount(data.created);
      setStep(3);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengimpor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden page-enter" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold">Langkah {step}/3</div>
            <h3 className="font-bold text-sm sm:text-base text-[#173042] mt-0.5">Impor Kontak dari Excel</h3>
          </div>
          <button onClick={onClose} data-testid="import-modal-close" className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#E2E8F0]"><X size={18} weight="bold" /></button>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
          {step === 1 && (
            <div className="space-y-4 sm:space-y-6">
              <label
                htmlFor="xlsx-file"
                data-testid="import-dropzone"
                className="block border-2 border-dashed border-[#CBD5E1] rounded-xl hover:border-[#173B4D] hover:bg-[#F8FAFC] transition-colors p-6 sm:p-10 text-center cursor-pointer"
              >
                <FileXls size={36} className="mx-auto text-[#2E8B57]" />
                <div className="mt-2 text-xs sm:text-sm text-[#173042]">Klik atau seret file <b>.xlsx</b> ke sini</div>
                <div className="mt-1 text-[11px] text-[#64748B]">Maksimal 5MB · baris pertama = nama kolom</div>
                <input id="xlsx-file" type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onFile} data-testid="import-file-input" />
              </label>
              <div className="border border-[#E2E8F0] rounded-xl p-3.5 sm:p-4 bg-[#F8FAFC]">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold mb-1.5">Format kolom yang dikenali</div>
                <div className="font-mono-data text-xs text-[#173042] break-all">
                  name · email · phone · company · position · status · source · notes
                </div>
                <div className="text-[11px] text-[#64748B] mt-1.5">Kolom lain diabaikan. Hanya baris dengan <b>name</b> yang diimpor.</div>
              </div>
              {loading && <div className="text-xs text-[#64748B] uppercase tracking-[0.15em] font-semibold text-center">Memproses file...</div>}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="border border-[#E2E8F0] rounded-xl p-3.5 flex items-center justify-between bg-[#F8FAFC]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">File</div>
                  <div className="text-xs sm:text-sm font-semibold text-[#173042] truncate max-w-[180px] sm:max-w-xs">{file?.name}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Baris siap impor</div>
                  <div className="font-mono-data text-xl sm:text-2xl font-bold text-[#0F7180]" data-testid="import-count">{rows.length}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#64748B] mb-1.5">Preview (5 baris pertama)</div>
                <div className="border border-[#E2E8F0] rounded-lg overflow-x-auto">
                  <table className="w-full text-xs min-w-[400px]">
                    <thead>
                      <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                        <th className="text-left p-2 font-bold uppercase text-[10px] tracking-[0.1em] text-[#64748B]">Nama</th>
                        <th className="text-left p-2 font-bold uppercase text-[10px] tracking-[0.1em] text-[#64748B]">Perusahaan</th>
                        <th className="text-left p-2 font-bold uppercase text-[10px] tracking-[0.1em] text-[#64748B]">Telepon</th>
                        <th className="text-left p-2 font-bold uppercase text-[10px] tracking-[0.1em] text-[#64748B]">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-b border-[#E2E8F0]">
                          <td className="p-2 font-medium text-[#173042]">{r.name}</td>
                          <td className="p-2 text-[#64748B]">{r.company || "-"}</td>
                          <td className="p-2 font-mono-data text-[#64748B]">{r.phone || "-"}</td>
                          <td className="p-2 font-mono-data text-[#64748B]">{r.email || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#64748B] mb-2">Pilih layanan (diterapkan ke semua kontak impor)</div>
                <div className="flex flex-wrap gap-1.5" data-testid="import-services-picker">
                  {services.map((s) => {
                    const active = selectedServices.includes(s.name);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleService(s.name)}
                        data-testid={`import-service-${s.name}`}
                        className={`inline-flex items-center gap-1 border px-2.5 py-1 text-[11px] uppercase tracking-[0.05em] font-semibold rounded-md transition-colors ${
                          active ? "bg-[#173B4D] border-[#173B4D] text-white shadow-2xs" : "border-[#CBD5E1] text-[#64748B] hover:border-[#173B4D]"
                        }`}
                      >
                        {active && <CheckCircle size={12} weight="fill" />} {s.name}
                      </button>
                    );
                  })}
                </div>
                {canAddService && (
                  <div className="mt-2.5 flex gap-2">
                    <input
                      value={newSvc}
                      onChange={(e) => setNewSvc(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addService(); } }}
                      placeholder="Tambah layanan baru..."
                      className="flex-1 rounded-lg border border-[#CBD5E1] px-3 py-1.5 text-xs text-[#173042] focus:outline-none focus:border-[#173B4D]"
                    />
                    <button type="button" onClick={addService} className="rounded-lg border border-[#CBD5E1] bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#173042] hover:bg-[#F8FAFC]">
                      Tambah
                    </button>
                  </div>
                )}
              </div>

              {currentUser?.role !== "sales" && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#64748B] mb-1.5">Assign semua ke</div>
                  <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="w-full rounded-lg border border-[#CBD5E1] px-3 py-2 text-xs text-[#173042] focus:outline-none focus:border-[#173B4D]" data-testid="import-owner-select">
                    <option value="">Saya</option>
                    {users.filter((u) => u.role !== "admin").map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-2.5 pt-3 border-t border-[#E2E8F0]">
                <button type="button" onClick={() => { setStep(1); setRows([]); setFile(null); }} className="px-3.5 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold uppercase tracking-wider text-[#64748B] hover:text-[#173042] flex items-center gap-1.5">
                  <ArrowLeft size={14} /> Kembali
                </button>
                <button
                  type="button"
                  onClick={commit}
                  disabled={loading}
                  data-testid="import-commit-btn"
                  className="flex-1 bg-[#173B4D] hover:bg-[#112B38] text-white px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider disabled:opacity-60 flex items-center justify-center gap-1.5 shadow-xs transition-colors"
                >
                  <UploadSimple size={15} /> {loading ? "Mengimpor..." : `Impor ${rows.length} Kontak`}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-6 sm:py-8">
              <CheckCircle size={56} weight="fill" className="mx-auto text-[#2E8B57]" />
              <div className="font-display text-2xl sm:text-3xl mt-3 text-[#173042]">Berhasil!</div>
              <div className="text-[#64748B] mt-1.5 text-xs sm:text-sm">
                <span className="font-mono-data text-[#173042] font-bold text-base sm:text-lg" data-testid="import-success-count">{importedCount}</span> kontak berhasil diimpor.
              </div>
              <button onClick={onClose} className="mt-5 bg-[#173B4D] hover:bg-[#112B38] text-white px-6 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider shadow-xs transition-colors">
                Selesai
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
